import { PATIENT_FRIENDLY_ROUTES, PATIENT_FRIENDLY_UNITS, type PatientFriendlyRoute } from '@liza/fhir-utils';
import { useForm } from '@tanstack/react-form';
import { Minus, PencilIcon, PillBottle } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { cn } from '../../utils/cn';
import { getCategoryGradient, getItemKindColor } from '../../utils/ringCategories';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import type { UseValueSetExpandResult } from '../../hooks/useValueSetExpand';
import { shouldPreserveCurrentFieldValue } from './medicationFormSeeding';
import {
  DueDateFieldSchema,
  TaskTimingPicker,
  type InitialTimingValue,
  type ScheduleType,
  type TimingRepeatValue,
  SHARED_SCHEDULE_TYPES,
} from '../TaskTimingPicker';
import { Button } from '../ui/button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../ui/tooltip';
import { Field, FieldGroup, FieldLabel } from '../ui/field';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Separator } from '../ui/separator';
import { Spinner } from '../ui/spinner';
import { isFreeTextItem, MedicationProductSearchField, type MedicationSearchQueries } from './medicationSearch';
import { ValueSetCombobox, type ValueSetCode } from './ValueSetCombobox';

export type { ValueSetCode };
export type { PatientFriendlyRoute };

const UNIT_VALUES = PATIENT_FRIENDLY_UNITS as [string, ...string[]];
const ROUTE_DISPLAYS = PATIENT_FRIENDLY_ROUTES.map((r) => r.display) as [string, ...string[]];

const formSchema = z.object({
  product: z.custom<ValueSetCode | undefined>().refine((v): v is ValueSetCode => v !== undefined),
  dose: z.number().positive(),
  // Units and Route are validated against the patient-friendly lookup table — only curated values
  // submit. Route stores the display phrase; its SNOMED code is reattached by the consumer at write.
  units: z.enum(UNIT_VALUES),
  route: z.enum(ROUTE_DISPLAYS),
  instructions: z.string().trim(),
  timing: z.custom<TimingRepeatValue | null>(),
  dueDate: DueDateFieldSchema,
  asNeeded: z.boolean(),
});

export type MedicationFormValues = z.infer<typeof formSchema>;

/**
 * Prefill hints used when the form is opened to confirm an AI-drafted medication. These are
 * typeahead seeds, **not** pre-selected coded values — the patient still has to pick a coded
 * product before the form can submit (schema unchanged).
 */
export interface MedicationFormDefaultValues {
  /** Seeds the product combobox query (RxNorm typeahead filter + visible input text). */
  medicationName?: string;
  /** Prefills the dose field. Parsed best-effort via `Number()`; left blank if not a positive number. */
  doseAmount?: string;
  /** Prefills the additional-instructions field. */
  instructions?: string;
}

/**
 * Add Medication form.
 *
 * @remarks
 * Units and Route are editable. `defaultUnit`/`defaultRoute` are derived by the parent from the
 * selected RxNorm product (via the `dose_form_of` lookup → friendly table) and pushed into the
 * fields whenever they change — so picking a product seeds them, but the user can override.
 */
export interface MedicationFormInitialValues {
  /** Stored scheduling mode, for the case the picker cannot infer — a dateless "Once". */
  scheduleType?: ScheduleType;
  product?: ValueSetCode;
  dose?: number;
  /** Patient-friendly unit (e.g. "tablet"); must be a `PATIENT_FRIENDLY_UNITS` value. */
  units?: string;
  /** Patient-friendly route display (e.g. "by mouth"); must be a `PATIENT_FRIENDLY_ROUTES` display. */
  route?: string;
  instructions?: string;
  /** Pre-fill the timing picker for edit mode. */
  timing?: InitialTimingValue;
  /** Pre-fill the due date for edit mode (YYYY-MM-DD). */
  dueDate?: string;
  /** Pre-select the "As needed" (PRN) option for edit mode. */
  asNeeded?: boolean;
}

/**
 * Static product representation shown when editing. The medication, dose, units, and route are
 * locked by default and summarized under the name; the edit button reveals the pickers so they
 * can be changed. When `onEdit` is omitted (e.g. a prescribed, vendor-owned med) the pencil is
 * hidden and the summary is purely read-only.
 */
const renderProductHeader = (
  initialValues: MedicationFormInitialValues | undefined,
  defaultValues: MedicationFormDefaultValues | undefined,
  fallbackLabel: string,
  editLabel: string,
  onEdit?: () => void,
): React.ReactNode => {
  const name = initialValues?.product?.display ?? defaultValues?.medicationName;
  const summary = [initialValues?.dose, initialValues?.units, initialValues?.route].filter(Boolean).join(' · ');
  return (
    <div className="flex items-center gap-3">
      <div
        className="flex size-12 shrink-0 items-center justify-center rounded-lg"
        style={{ background: getCategoryGradient(getItemKindColor('medication')), color: 'var(--background)' }}
      >
        <PillBottle className="size-5" />
      </div>
      <div className="min-w-0 flex-1">
        <p className="truncate text-base font-medium">{name || fallbackLabel}</p>
        {summary && <p className="truncate text-sm text-muted-foreground">{summary}</p>}
      </div>
      {onEdit && (
        <Button type="button" size="icon-lg" variant="ghost" onClick={onEdit} aria-label={editLabel}>
          <PencilIcon className="size-4" />
        </Button>
      )}
    </div>
  );
};

export interface MedicationFormProps {
  onCreate: (values: MedicationFormValues) => Promise<void>;
  onClose: () => void;
  isCreating: boolean;
  /** Data source for the legacy RxNorm ValueSet product typeahead. Required unless
   * `medicationSearch` is provided, which replaces the product picker entirely. */
  product?: UseValueSetExpandResult;
  /**
   * ScriptSure drug-search query factories. When provided, the product picker becomes the two-step
   * drug → strength/form search (with free-text fallback) instead of the RxNorm ValueSet typeahead.
   * A free-text pick lands in the `product` field as a sentinel `ValueSetCode` (`isFreeTextItem`) —
   * consumers map it to `{ text }` on submit via `medicationInputFromProduct`.
   *
   * Current limitations (fine for the add flows using it today; revisit before wider adoption):
   * the search picker ignores `defaultValues.medicationName` (no typeahead seeding for
   * draft-confirm flows), and it starts empty — combining it with an `initialValues.product`
   * edit pre-fill would wipe the pre-filled product the moment the pickers unlock.
   */
  medicationSearch?: MedicationSearchQueries;
  /** Option set for the Units select (patient-friendly units from the lookup table). */
  unitOptions: string[];
  /** Option set for the Route select (patient-friendly routes from the lookup table). */
  routeOptions: PatientFriendlyRoute[];
  /** Dose derived from the selected product's ScriptSure sig; seeds the Dose field when it changes.
   * Optional — flows without a vendor sig (the RxNorm ValueSet picker) simply omit it. */
  defaultDose?: { value: number | null };
  /** Unit derived from the selected product (sig, else dose form); seeds the Units field. */
  defaultUnit: { value: string | null; isLoading: boolean };
  /** Route derived from the selected product (sig, else dose form); seeds the Route field. */
  defaultRoute: { value: PatientFriendlyRoute | null; isLoading: boolean };
  /** Notifies the parent when the product selection changes so it can drive the dose-form lookup.
   * `sigLine` is the chosen formulation's ScriptSure sig template, when it has one. */
  onProductChange: (code: ValueSetCode | null, sigLine?: string) => void;
  /** Schedule types exposed in the TaskTimingPicker. Defaults to every mode — a medication can be
   * on a schedule, as needed (PRN), or a single dose (Once). */
  supportedScheduleTypes?: readonly ScheduleType[];
  /** Whether the caller persists `dueDate`. False hides the due-date field on "Once", for a caller
   * whose endpoint has no due-date concept. */
  supportsDueDate?: boolean;
  /** Initial schedule type. Defaults to 'simple' — most medications are taken on an ongoing schedule. */
  defaultScheduleType?: ScheduleType;
  /** When true, submit/cancel buttons read "Save"/"Dismiss" instead of "Add"/"Cancel". */
  isDraft?: boolean;
  /** Typeahead/prefill hints for draft confirmation. Schema is unchanged — all four coded fields remain required. */
  defaultValues?: MedicationFormDefaultValues;
  /** Pre-fills the form for edit flows. */
  initialValues?: MedicationFormInitialValues;
  /** When provided, renders a "Remove" button alongside submit/cancel. */
  onRemove?: () => void;
  /** Optional label override (e.g. 'save' for edit mode). */
  submitLabel?: string;
  /** When true, the submit button is disabled regardless of form validity. */
  submitDisabled?: boolean;
  /** Root className override — e.g. drop the default `p-6` when the dialog already provides padding. */
  className?: string;
}

/**
 * Best-effort parse of the draft dose. Schema requires a positive number; anything else
 * (undefined, NaN, 0, negative, "10mg") falls back to undefined so the field renders blank.
 * `Number(undefined)` is `NaN`, so the absent case folds into the same branch.
 */
function parseInitialDose(doseAmount: string | undefined): number | undefined {
  const parsed = Number(doseAmount);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : undefined;
}

function buildDefaultValues(
  initialValues: MedicationFormInitialValues | undefined,
  defaultValues: MedicationFormDefaultValues | undefined,
  initialDose: number | undefined,
): {
  product: ValueSetCode | undefined;
  dose: number | undefined;
  units: string | undefined;
  route: string | undefined;
  instructions: string;
  timing: TimingRepeatValue | null;
  dueDate: string;
  asNeeded: boolean;
} {
  return {
    product: initialValues?.product,
    dose: initialValues?.dose ?? initialDose,
    units: initialValues?.units,
    route: initialValues?.route,
    instructions: initialValues?.instructions ?? defaultValues?.instructions ?? '',
    timing: null,
    dueDate: '',
    asNeeded: initialValues?.asNeeded ?? false,
  };
}

export function MedicationForm({
  onCreate,
  onClose,
  isCreating,
  product,
  medicationSearch,
  unitOptions,
  routeOptions,
  defaultDose,
  defaultUnit,
  defaultRoute,
  onProductChange,
  supportedScheduleTypes = SHARED_SCHEDULE_TYPES,
  supportsDueDate = true,
  defaultScheduleType = 'simple',
  isDraft = false,
  defaultValues,
  initialValues,
  onRemove,
  submitLabel,
  submitDisabled,
  className,
}: MedicationFormProps): React.ReactNode {
  const { t } = useTranslation('records');
  const { t: tCommon } = useTranslation('common');

  // Editing an existing medication (an initial product is present) locks the medication, dose, form,
  // and route to a static summary header; the edit button unlocks the pickers. Add flows have no
  // initial product and always show the pickers. (Keyed on the product, not `onRemove`, so flows
  // that hide Remove — e.g. the cross-care-plan Meds and schedule views — still get the header.)
  const [isEditingProduct, setIsEditingProduct] = useState(false);
  const showProductFields = !initialValues?.product || isEditingProduct;

  const initialDose = parseInitialDose(defaultValues?.doseAmount);

  const formApi = useForm({
    defaultValues: buildDefaultValues(initialValues, defaultValues, initialDose),
    validators: {
      onMount: formSchema,
      onChange: formSchema,
      onSubmit: formSchema,
    },
    onSubmit: async ({ value }) => {
      await onCreate(formSchema.parse(value));
    },
  });

  // Seed Units + Route when the parent's derived values resolve — i.e., the product's `dose_form_of`
  // lookup → friendly table returns. Guarded against null so the initial mount (no product yet)
  // doesn't clobber an `initialValues` pre-fill used by the edit flow. Whether a resolution
  // overwrites the field or keeps its current value lives in `shouldPreserveCurrentFieldValue`. A
  // *user-initiated product change* always overwrites (the field still holds the previous product's
  // value) — each field carries its own consume-once flag, set on product change and cleared when
  // that field's next derivation lands. Any other resolution (initial edit-flow seed, background
  // refetch) keeps a current value that is already a valid option.
  //
  // Reading the field's *current* value (not `initialValues`) also makes this idempotent under a
  // StrictMode double-invoke: the second invoke sees the value the first just wrote, which is a
  // valid option, so it preserves rather than re-deriving.
  const doseOverridePending = useRef(false);
  const unitOverridePending = useRef(false);
  const routeOverridePending = useRef(false);

  // A product change must always either re-seed a field or blank it — never leave the *previous*
  // product's value behind. Deriving nothing is a meaningful outcome, not a no-op: it means this
  // product states no dose/unit/route and the prescriber must supply one. Switching a device product
  // (which derives "1 unit / by injection") for a vial (which deliberately derives nothing) would
  // otherwise carry the injector's dosage onto the vial — exactly the guess this feature avoids.
  //
  // Only a *pending product change* clears. Any other null derivation (initial mount before a product
  // is chosen, a background refetch) leaves the field alone, so an `initialValues` edit pre-fill and a
  // value the prescriber typed both survive.

  // Dose is a free numeric field rather than a closed option set, so `shouldPreserveCurrentFieldValue`
  // (which asks "is the current value still a valid option?") doesn't apply: any number is valid.
  // Seed it only when the field is empty, or when the product just changed and the field still holds
  // the previous product's dose — never overwrite a number the prescriber typed.
  const defaultDoseValue = defaultDose?.value ?? null;
  useEffect(() => {
    const productChanged = doseOverridePending.current;
    if (defaultDoseValue === null) {
      if (productChanged) {
        doseOverridePending.current = false;
        formApi.setFieldValue('dose', undefined);
      }
      return;
    }
    doseOverridePending.current = false;
    if (productChanged || formApi.getFieldValue('dose') === undefined) {
      formApi.setFieldValue('dose', defaultDoseValue);
    }
  }, [defaultDoseValue, formApi]);

  const defaultUnitValue = defaultUnit.value;
  useEffect(() => {
    const productChanged = unitOverridePending.current;
    if (!defaultUnitValue) {
      if (productChanged) {
        unitOverridePending.current = false;
        formApi.setFieldValue('units', '');
      }
      return;
    }
    const preserve = shouldPreserveCurrentFieldValue({
      productChanged,
      currentValue: formApi.getFieldValue('units'),
      options: unitOptions,
    });
    unitOverridePending.current = false;
    if (!preserve) {
      formApi.setFieldValue('units', defaultUnitValue);
    }
  }, [defaultUnitValue, formApi, unitOptions]);

  const defaultRouteDisplay = defaultRoute.value?.display;
  useEffect(() => {
    const productChanged = routeOverridePending.current;
    if (!defaultRouteDisplay) {
      if (productChanged) {
        routeOverridePending.current = false;
        formApi.setFieldValue('route', '');
      }
      return;
    }
    const preserve = shouldPreserveCurrentFieldValue({
      productChanged,
      currentValue: formApi.getFieldValue('route'),
      options: routeOptions.map((option) => option.display),
    });
    routeOverridePending.current = false;
    if (!preserve) {
      formApi.setFieldValue('route', defaultRouteDisplay);
    }
  }, [defaultRouteDisplay, formApi, routeOptions]);

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        void formApi.handleSubmit();
      }}
      className={cn('space-y-4 p-6', className)}
    >
      <FieldGroup>
        {/* Editing an existing item: the medication, dose, form, and route collapse to a static
            summary header by default, with an edit button to reveal the pickers below. */}
        {showProductFields ? (
          <formApi.Field name="product">
            {(field) => {
              const handleProductChange = (value: ValueSetCode | null, sigLine?: string): void => {
                field.handleChange(value ?? undefined);
                // A free-text sentinel has no RxNorm code — don't feed it to the dose-form lookup.
                onProductChange(value && !isFreeTextItem(value) ? value : null, sigLine);
                // The new product's sig/dose form should drive Dose/Units/Route, overriding the values
                // the previous product (or the edit pre-fill) left in those fields.
                doseOverridePending.current = true;
                unitOverridePending.current = true;
                routeOverridePending.current = true;
              };
              if (medicationSearch) {
                return <MedicationProductSearchField queries={medicationSearch} onValueChange={handleProductChange} />;
              }
              if (!product) {
                // Neither picker was configured — the schema still requires a product, so the form
                // would render submittable-looking but be permanently unsubmittable. Fail fast.
                throw new Error('MedicationForm requires either `product` or `medicationSearch`');
              }
              return (
                <ValueSetCombobox
                  id={field.name}
                  label={t('product')}
                  items={product.items}
                  value={field.state.value ?? null}
                  onValueChange={handleProductChange}
                  onQueryChange={product.onQueryChange}
                  isLoading={product.isFetching}
                  error={product.error}
                  emptyMessage={t('noMatchingResults')}
                  required
                  initialQuery={defaultValues?.medicationName}
                />
              );
            }}
          </formApi.Field>
        ) : (
          renderProductHeader(initialValues, defaultValues, tCommon('medication'), tCommon('edit'), () =>
            setIsEditingProduct(true),
          )
        )}

        {showProductFields && (
          <div className="flex gap-3 *:flex-1">
            <formApi.Field name="dose">
              {(field) => (
                <Field>
                  <FieldLabel htmlFor={field.name}>
                    {t('dose')}
                    <span aria-hidden="true">*</span>
                  </FieldLabel>
                  <Input
                    id={field.name}
                    name={field.name}
                    type="number"
                    inputMode="decimal"
                    required
                    value={field.state.value ?? ''}
                    onBlur={field.handleBlur}
                    onChange={(e) => field.handleChange(e.target.value === '' ? undefined : e.target.valueAsNumber)}
                    aria-label={t('dose')}
                  />
                </Field>
              )}
            </formApi.Field>

            <formApi.Field name="units">
              {(field) => (
                <Field>
                  <FieldLabel htmlFor={field.name}>
                    {t('units')}
                    <span aria-hidden="true">*</span>
                  </FieldLabel>
                  <Select
                    value={field.state.value ?? ''}
                    onValueChange={(value) => field.handleChange(value)}
                    disabled={defaultUnit.isLoading}
                  >
                    <SelectTrigger id={field.name} aria-label={t('units')}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {unitOptions.map((unit) => (
                        <SelectItem key={unit} value={unit}>
                          {unit}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </Field>
              )}
            </formApi.Field>

            <formApi.Field name="route">
              {(field) => (
                <Field>
                  <FieldLabel htmlFor={field.name}>
                    {t('route')}
                    <span aria-hidden="true">*</span>
                  </FieldLabel>
                  <Select
                    value={field.state.value ?? ''}
                    onValueChange={(value) => field.handleChange(value)}
                    disabled={defaultRoute.isLoading}
                  >
                    <SelectTrigger id={field.name} aria-label={t('route')}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {routeOptions.map((r) => (
                        <SelectItem key={r.display} value={r.display}>
                          {r.display}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </Field>
              )}
            </formApi.Field>
          </div>
        )}
      </FieldGroup>

      <Separator />

      <formApi.Field name="timing">
        {(timingField) => (
          <TaskTimingPicker
            defaultScheduleType={defaultScheduleType}
            supportedScheduleTypes={supportedScheduleTypes}
            supportsDueDate={supportsDueDate}
            initialTiming={initialValues?.timing}
            initialDueDate={initialValues?.dueDate}
            initialAsNeeded={initialValues?.asNeeded}
            initialScheduleType={initialValues?.scheduleType}
            onChange={(timing, dueDate, asNeeded) => {
              timingField.handleChange(timing);
              formApi.setFieldValue('dueDate', dueDate);
              formApi.setFieldValue('asNeeded', asNeeded);
            }}
            onValidChange={(valid) =>
              // `as never`: TanStack Form types `onChange` errors as the validator's output,
              // but we surface a plain string here for the user-visible message.
              timingField.setErrorMap({ onChange: valid ? undefined : ('Invalid timing' as never) })
            }
          />
        )}
      </formApi.Field>

      <FieldGroup>
        <formApi.Field name="instructions">
          {(field) => (
            <Field>
              <FieldLabel htmlFor={field.name}>{t('additionalInstructions')}</FieldLabel>
              <Input
                id={field.name}
                name={field.name}
                value={field.state.value}
                onBlur={field.handleBlur}
                onChange={(e) => field.handleChange(e.target.value)}
                aria-label={t('additionalInstructions')}
              />
            </Field>
          )}
        </formApi.Field>
      </FieldGroup>

      <div className="flex items-center gap-2">
        {/* Remove stays available for a prescribed med (detach from the topic); only edit is locked. */}
        {onRemove && (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  type="button"
                  size="icon-lg"
                  variant="ghost"
                  onClick={onRemove}
                  disabled={isCreating}
                  className="text-destructive hover:bg-destructive/10 hover:text-destructive"
                  aria-label={tCommon('removeFromPlan')}
                  data-testid="medication-remove"
                >
                  <Minus className="size-5" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>{tCommon('removeFromPlan')}</TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}
        <Button
          type="button"
          size="lg"
          variant="ghost"
          onClick={onClose}
          disabled={isCreating}
          className="ml-auto"
          aria-label={isDraft ? t('dismiss') : t('cancelAddMedication')}
        >
          {isDraft ? t('dismiss') : t('cancel')}
        </Button>
        <formApi.Subscribe selector={(state) => [state.canSubmit, state.isSubmitting] as const}>
          {([canSubmit, isSubmitting]) => {
            const label = submitLabel ? tCommon(submitLabel) : isDraft ? t('save') : t('add');
            return (
              <Button
                type="submit"
                size="lg"
                disabled={!canSubmit || isSubmitting || isCreating || submitDisabled}
                aria-label={isDraft ? t('save') : t('saveNewMedication')}
              >
                {isSubmitting || isCreating ? <Spinner className="size-4" /> : label}
              </Button>
            );
          }}
        </formApi.Subscribe>
      </div>
    </form>
  );
}
